import { useLocation } from "react-router-dom";
import { useQuery } from "react-query";
import { makeAuthedRequest } from "../utils/makeAuthedRequest";
import { parseApiError } from "../utils/apiError";

function toValidContactId(value) {
  if (value == null || value === "") return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
}

function resolveContactId(user, location) {
  const params = new URLSearchParams(location.search);
  const queryContactId = params.get("contactId");
  const parsedQueryContactId = toValidContactId(queryContactId);
  if (parsedQueryContactId != null) return parsedQueryContactId;

  // Profile returns { users: [...], isFreeTrial }, an array of users, or a single user object
  if (Array.isArray(user?.users)) {
    return toValidContactId(user.users[0]?.contactId);
  }
  if (Array.isArray(user)) return toValidContactId(user[0]?.contactId);
  if (Array.isArray(user?.contacts)) {
    return toValidContactId(user.contacts[0]?.contactId);
  }
  return toValidContactId(user?.contactId);
}

// Resolves the active contactId: prefers ?contactId= query param, falls back to contacts[0]
export function useContactId() {
  return useContactIdStatus().contactId;
}

// Same as useContactId, but also exposes whether we're genuinely still
// waiting on contactId to resolve, so callers can stop treating "waiting" as
// permanent once the profile query has settled either way (success or
// error) and still yielded no usable contactId - e.g. a new IBO with no
// linked contacts yet, an unexpected payload shape, or a real fetch failure.
export function useContactIdStatus() {
  const location = useLocation();

  const { data: user, status } = useQuery("user", async () => {
    const res = await makeAuthedRequest("/user/profile");
    if (!res.ok) throw await parseApiError(res, "User fetch failed");
    return res.json();
  });

  const contactId = resolveContactId(user, location);
  // Still genuinely pending: no contactId yet, and the profile query hasn't
  // settled (succeeded or errored) to tell us it never will resolve one.
  const isContactIdPending =
    contactId == null && status !== "success" && status !== "error";

  return { contactId, isContactIdPending };
}

// Single source of truth for gating a contactId-dependent query. Consumers
// MUST use `gateOpen` verbatim as (part of) their useQuery `enabled` option,
// and `gatePending` verbatim as (part of) the isIdle fold condition on the
// returned query - never hand-write an equivalent boolean expression for
// either, so the two can't drift out of sync the way they repeatedly did
// during review of this hook.
//
// - `gateOpen`: true once the query should actually fire (contactId resolved,
//   or the caller opted out of the contactId requirement via `bypass`, e.g.
//   a mock-data code path that never reads contactId).
// - `gatePending`: true only while there is still a genuine chance the gate
//   will open on its own (contactId hasn't resolved yet AND the underlying
//   profile query hasn't settled to "no contactId available"). Once the
//   profile query settles with no usable contactId, or errors outright,
//   this goes false so callers can stop waiting and reach a terminal state
//   instead of hanging in a permanent loading fold.
export function useContactGate({ bypass = false } = {}) {
  const { contactId, isContactIdPending } = useContactIdStatus();
  return {
    contactId,
    gateOpen: bypass || contactId != null,
    gatePending: !bypass && isContactIdPending,
  };
}
